import { defaults } from 'underscore';
import Promise from 'bluebird';

import { getAgeGenderGridConfig } from './ageGender/ageGender-grid-config';
import { getCallForwardingGridConfig } from './callforwarding/callforwarding-grid-config';
import { getGeographicGridConfig } from './geographic/geographic-grid-config';
import { getLandingGridConfig } from './landing/landing-grid-config';
import { getPublisherGridConfig } from './publisher/publisher-grid-config';
import { getSearchTermGridConfig } from './searchterm/searchterm-grid-config';
import { getShoppingGridConfig } from './shopping/shopping-grid-config';
import { getTimeGridConfig } from './time/time-grid-config';
import { getKeywordsLabelGridConfig } from './labels/keyword/keyword-label-grid-config';
import { getAdLabelsGridConfig } from './labels/ad/ad-label-grid-config';
import { getAdGroupLabelsGridConfig } from './labels/ad-group/ad-group-label-grid-config';
import { getCampaignLabelsGridConfig } from './labels/campaign/campaign-label-grid-config';
import { getAccountLabelsGridConfig } from './labels/account/account-label-grid-config';
import { Constants } from './constants';

function getDimensionsGridConfig({
  gridType,
  legacyDeps,
  newI18n,
  schemaLoaders,
  useAsyncSchemaLoaders = true,
  entitySchemas = {},
  isForVNext = false,
}) {
  switch (gridType) {
    case Constants.Time:
    case Constants.TimeHour:
    case Constants.TimeDay:
    case Constants.TimeDate:
    case Constants.TimeWeek:
    case Constants.TimeMonth:
    case Constants.TimeQuarter:
    case Constants.TimeYear:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getTimeSchema().then(timeSchema => (
          getTimeGridConfig(defaults({}, { timeSchema, gridType, isForVNext }, legacyDeps))
        ));
      }
      return getTimeGridConfig(defaults({}, {
        timeSchema: entitySchemas.Time,
        gridType,
        isForVNext,
      }, legacyDeps));
    case Constants.SearchQuery:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getSearchTermSchema().then(searchTermSchema => (
          getSearchTermGridConfig(defaults({}, {
            searchTermSchema, gridType, isForVNext,
          }, legacyDeps))
        ));
      }
      return getSearchTermGridConfig(defaults({}, {
        searchTermSchema: entitySchemas.SearchTerm, gridType, isForVNext,
      }, legacyDeps));
    case Constants.Shopping:
    case Constants.ShoppingCategory:
    case Constants.ShoppingProductType:
    case Constants.ShoppingBrand:
    case Constants.ShoppingItem:
    case Constants.ShoppingStore:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getShoppingSchema().then(shoppingSchema => (
          getShoppingGridConfig(defaults({}, { shoppingSchema, gridType, isForVNext }, legacyDeps))
        ));
      }
      return getShoppingGridConfig(defaults({}, {
        shoppingSchema: entitySchemas.Shopping, gridType, isForVNext,
      }, legacyDeps));
    case Constants.Publisher:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getPublisherSchema().then(publisherSchema => (
          getPublisherGridConfig(defaults({}, {
            publisherSchema, gridType, isForVNext,
          }, legacyDeps))
        ));
      }
      return getPublisherGridConfig(defaults({}, {
        publisherSchema: entitySchemas.Publisher, gridType, isForVNext,
      }, legacyDeps));
    case Constants.LandingPage:
    case Constants.WebsiteURL:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getLandingSchema().then(landingSchema => (
          getLandingGridConfig(defaults({}, { landingSchema, gridType, isForVNext }, legacyDeps))
        ));
      }
      return getLandingGridConfig(defaults({}, {
        landingSchema: entitySchemas.Landing, gridType, isForVNext,
      }, legacyDeps));
    case Constants.Geographic:
    case Constants.UserLocation:
      if (useAsyncSchemaLoaders) {
        return Promise.props({
          geographicSchema: schemaLoaders.getGeographicSchema(),
          locationSchema: schemaLoaders.getLocationSchema(),
        }).then(({ geographicSchema, locationSchema }) => (
          getGeographicGridConfig(defaults({}, {
            geographicSchema,
            locationSchema,
            gridType,
            isForVNext,
          }, legacyDeps))
        ));
      }
      return getGeographicGridConfig(defaults({}, {
        geographicSchema: entitySchemas.Geographic,
        locationSchema: entitySchemas.Location,
        gridType,
        isForVNext,
      }, legacyDeps));
    case Constants.CallDetails:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getCallForwardingSchema().then(callForwardingSchema => (
          getCallForwardingGridConfig(defaults({}, {
            callForwardingSchema, gridType, isForVNext,
          }, legacyDeps))
        ));
      }
      return getCallForwardingGridConfig(defaults({}, {
        callForwardingSchema: entitySchemas.CallForwarding, gridType, isForVNext,
      }, legacyDeps));
    case Constants.AgeGender:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getAgeSchema().then(ageSchema => (
          getAgeGenderGridConfig(defaults({}, { ageSchema, gridType, isForVNext }, legacyDeps))
        ));
      }
      return getAgeGenderGridConfig(defaults({}, {
        ageSchema: entitySchemas.Age, gridType, isForVNext,
      }, legacyDeps));
    case Constants.LabelKeyword:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getKeywordsLabelSchema().then(labelKeywordsSchema => (
          getKeywordsLabelGridConfig(defaults(
            {},
            {
              labelKeywordsSchema, gridType, isForVNext, newI18n,
            }, legacyDeps
          ))
        ));
      }
      return getKeywordsLabelGridConfig(defaults(
        {},
        {
          labelKeywordsSchema: entitySchemas.LabelKeyword, gridType, isForVNext, newI18n,
        }, legacyDeps
      ));
    case Constants.LabelAd:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getAdLabelSchema().then(LabelAdSchema => (
          getAdLabelsGridConfig(defaults(
            {},
            {
              LabelAdSchema, gridType, isForVNext, newI18n,
            }, legacyDeps
          ))
        ));
      }
      return getAdLabelsGridConfig(defaults(
        {},
        {
          LabelAdSchema: entitySchemas.LabelAd, gridType, isForVNext, newI18n,
        }, legacyDeps
      ));
    case Constants.LabelAdgroup:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getAdGroupLabelSchema().then(LabelAdGroupSchema => (
          getAdGroupLabelsGridConfig(defaults(
            {},
            {
              LabelAdGroupSchema, gridType, isForVNext, newI18n,
            }, legacyDeps
          ))
        ));
      }
      return getAdGroupLabelsGridConfig(defaults(
        {},
        {
          LabelAdGroupSchema: entitySchemas.LabelAdGroup, gridType, isForVNext, newI18n,
        }, legacyDeps
      ));
    case Constants.LabelCampaign:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getCampaignLabelSchema().then(LabelCampaignSchema => (
          getCampaignLabelsGridConfig(defaults(
            {},
            {
              LabelCampaignSchema, gridType, isForVNext, newI18n,
            }, legacyDeps
          ))
        ));
      }
      return getCampaignLabelsGridConfig(defaults(
        {},
        {
          LabelCampaignSchema: entitySchemas.LabelCampaign, gridType, isForVNext, newI18n,
        }, legacyDeps
      ));
    case Constants.LabelAccount:
      if (useAsyncSchemaLoaders) {
        return schemaLoaders.getAccountLabelSchema().then(LabelAccountSchema => (
          getAccountLabelsGridConfig(defaults(
            {},
            {
              LabelAccountSchema, gridType, isForVNext, newI18n,
            }, legacyDeps
          ))
        ));
      }
      return getAccountLabelsGridConfig(defaults(
        {},
        {
          LabelAccountSchema: entitySchemas.LabelAccount, gridType, isForVNext, newI18n,
        }, legacyDeps
      ));
    default:
      return Promise.resolve({});
  }
}

export { Constants, getDimensionsGridConfig };
